import { FC } from 'react';
import type { MaquinaDia, MaquinaSerie, MfrAgregado, VisaoGeralMes } from '@/types/dashboards';
import { diasPorData, faixaMfr, semanaPorInicio, semanasDoMes, type SemanaDoMes } from '@/utils/equiplexMaquinas';
import { fmtDiaMes, fmtPct, fmtUn } from '@/utils/painelFormat';
import PainelCard from '../PainelCard';
import { fundoFaixa, visualFaixaMfr } from './visual';

interface Props {
  maquinas: MaquinaSerie[];
  total: MaquinaSerie | null;
  mes: VisaoGeralMes;
  limiares: { alerta: number; critico: number };
}

type Limiares = Props['limiares'];

const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const pctCurto = (v: number | null): string => (v === null ? '—' : `${nf0.format(v)}%`);
const BORDA_SEMANA = 'border-l border-theme-border';

function estiloFaixa(mfr: number | null, limiares: Limiares) {
  const faixa = faixaMfr(mfr, limiares);
  return faixa ? { backgroundColor: fundoFaixa(visualFaixaMfr(faixa).cor) } : undefined;
}

function tituloDia(data: string, d: MaquinaDia): string {
  const base = `${fmtDiaMes(data)} · programado ${fmtUn(d.programado)} · produzido ${fmtUn(d.produzido)} · ${d.ops} OP(s)`;
  if (d.situacao === 'futuro') return `${base} · ainda não começou`;
  const mfr = `MFR ${fmtPct(d.mfr)}`;
  return d.situacao === 'aberto' ? `${base} · ${mfr} · dia aberto: OPs ainda sendo reportadas` : `${base} · ${mfr}`;
}

const CelulaDia: FC<{ data: string; dia?: MaquinaDia; limiares: Limiares; primeira: boolean }> = ({ data, dia, limiares, primeira }) => {
  const borda = primeira ? BORDA_SEMANA : '';
  if (!dia) return <td className={`px-1 py-1.5 ${borda}`} />;
  if (dia.situacao === 'futuro') {
    return (
      <td className={`px-1 py-1.5 text-center text-content-muted ${borda}`} title={tituloDia(data, dia)}>
        ·
      </td>
    );
  }
  const aberto = dia.situacao === 'aberto';
  return (
    <td
      className={`whitespace-nowrap px-1 py-1.5 text-center tabular-nums ${aberto ? 'italic text-content-secondary' : 'text-content-primary'} ${borda}`}
      style={aberto ? undefined : estiloFaixa(dia.mfr, limiares)}
      title={tituloDia(data, dia)}
    >
      {pctCurto(dia.mfr)}
      {aberto && <span aria-hidden>°</span>}
    </td>
  );
};

const CelulaAgregado: FC<{ ag?: MfrAgregado; parcial?: boolean; limiares: Limiares; rotulo: string }> = ({
  ag,
  parcial,
  limiares,
  rotulo,
}) => (
  <td
    className={`whitespace-nowrap px-2 py-1.5 text-center font-semibold tabular-nums text-content-primary ${BORDA_SEMANA}`}
    style={ag ? estiloFaixa(ag.mfr, limiares) : undefined}
    title={ag ? `${rotulo} · programado ${fmtUn(ag.programado)} · produzido ${fmtUn(ag.produzido)} · ${ag.dias_fechados} dia(s) fechado(s)` : rotulo}
  >
    {ag ? pctCurto(ag.mfr) : '—'}
    {parcial && ag && ag.mfr !== null && <span aria-hidden>*</span>}
  </td>
);

const Linha: FC<{ rotulo: string; serie: MaquinaSerie; semanas: SemanaDoMes[]; limiares: Limiares; total?: boolean }> = ({
  rotulo,
  serie,
  semanas,
  limiares,
  total,
}) => {
  const dias = diasPorData(serie);
  const agregadas = semanaPorInicio(serie);
  return (
    <tr className={total ? 'border-t-2 border-theme-border font-semibold' : 'border-t border-theme-border'} data-testid={`mfr-linha-${rotulo}`}>
      <th scope="row" className="sticky left-0 z-10 whitespace-nowrap bg-surface-card py-1.5 pr-3 text-left font-medium text-content-primary">
        {rotulo}
      </th>
      {semanas.map((s) => {
        const ag = agregadas.get(s.inicio);
        return [
          ...s.dias.map((d, i) => <CelulaDia key={d} data={d} dia={dias.get(d)} limiares={limiares} primeira={i === 0} />),
          <CelulaAgregado key={`sem-${s.inicio}`} ag={ag} parcial={!!ag && !ag.completa} limiares={limiares} rotulo={`Semana ${fmtDiaMes(s.inicio)} a ${fmtDiaMes(s.fim)}`} />,
        ];
      })}
      <CelulaAgregado ag={serie.mes_total} parcial={false} limiares={limiares} rotulo="Mês (dias fechados)" />
    </tr>
  );
};

const Cabecalho: FC<{ semanas: SemanaDoMes[] }> = ({ semanas }) => (
  <thead className="text-xs text-content-muted">
    <tr>
      <th className="sticky left-0 z-10 bg-surface-card" />
      {semanas.map((s) => (
        <th key={s.inicio} colSpan={s.dias.length + 1} className={`whitespace-nowrap px-1 pb-1 text-center font-medium ${BORDA_SEMANA}`}>
          {fmtDiaMes(s.inicio)} a {fmtDiaMes(s.fim)}
        </th>
      ))}
      <th className={BORDA_SEMANA} />
    </tr>
    <tr className="uppercase tracking-wide">
      <th className="sticky left-0 z-10 bg-surface-card py-1 pr-3 text-left font-medium">Máquina</th>
      {semanas.map((s) => [
        ...s.dias.map((d, i) => (
          <th key={d} className={`px-1 py-1 text-center font-medium tabular-nums ${i === 0 ? BORDA_SEMANA : ''}`}>
            {d.slice(8)}
          </th>
        )),
        <th key={`sem-${s.inicio}`} className={`px-2 py-1 text-center font-semibold ${BORDA_SEMANA}`}>
          Sem.
        </th>,
      ])}
      <th className={`px-2 py-1 text-center font-semibold ${BORDA_SEMANA}`}>Mês</th>
    </tr>
  </thead>
);

const Legenda: FC<{ limiares: Limiares }> = ({ limiares }) => (
  <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-secondary" aria-label="Legenda">
    {(['bom', 'alerta', 'critico'] as const).map((f) => {
      const v = visualFaixaMfr(f);
      const faixa = {
        bom: `${limiares.alerta}% ou mais`,
        alerta: `${limiares.critico}% a ${limiares.alerta}%`,
        critico: `abaixo de ${limiares.critico}%`,
      }[f];
      return (
        <li key={f} className="flex items-center gap-1.5">
          <span aria-hidden className="material-symbols-outlined text-[15px] leading-none" style={{ color: v.cor }}>
            {v.icone}
          </span>
          {v.rotulo}: {faixa}
        </li>
      );
    })}
    <li>° dia aberto (OPs ainda sendo reportadas) · * semana ainda aberta · · só programado</li>
  </ul>
);

/**
 * A planilha do Attila, gerada: MFR por máquina e dia, com o fechamento de cada semana
 * (domingo a sábado) e do mês. Cor de fundo pela faixa, número sempre escrito.
 */
const MfrMatriz: FC<Props> = ({ maquinas, total, mes, limiares }) => {
  const semanas = semanasDoMes(mes);
  return (
    <PainelCard
      testId="maq-matriz"
      titulo="MFR por máquina e dia"
      subtitulo={`${mes.label} (${mes.range}). MFR = 1 − |programado − produzido| ÷ programado; semana e mês somam o desvio de cada dia.`}
    >
      <Legenda limiares={limiares} />
      {maquinas.length === 0 ? (
        <div className="py-10 text-center text-sm text-content-muted">Nenhuma OP neste mês.</div>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full text-xs">
            <Cabecalho semanas={semanas} />
            <tbody>
              {maquinas.map((s) => (
                <Linha key={s.maquina} rotulo={s.maquina ?? ''} serie={s} semanas={semanas} limiares={limiares} />
              ))}
              {total && <Linha rotulo="Total" serie={total} semanas={semanas} limiares={limiares} total />}
            </tbody>
          </table>
        </div>
      )}
    </PainelCard>
  );
};

export default MfrMatriz;
